import { useState } from "react";
import { toast } from "sonner";
import { Boxes, Zap, Sparkles, Map, ListChecks, Swords, TrendingUp, ScanSearch, Trash2, ChevronDown, CheckCircle2, Clock, Building2, Lightbulb } from "lucide-react";

const KIND_META = {
  roadmap: { label: "Roadmaps", icon: Map },
  plan: { label: "Validation Plans", icon: ListChecks },
  idea: { label: "Scanned Ideas", icon: Lightbulb },
  competitor: { label: "Competitor Analyses", icon: Swords },
  market: { label: "Market Size Estimates", icon: TrendingUp },
  name: { label: "Name Checks", icon: ScanSearch },
  formation: { label: "Formation Plans", icon: Building2 },
  stack: { label: "AI Stacks", icon: Boxes },
  workflow: { label: "Saved Workflows", icon: Zap },
  tool: { label: "Saved Tools", icon: Sparkles },
};
const ORDER = ["roadmap", "plan", "idea", "competitor", "market", "name", "formation", "stack", "workflow", "tool"];
const CHECKABLE = ["roadmap", "plan"];

export const AISavedNotebook = ({ items, onDelete, onUpdate }) => {
  const groups = ORDER.map((k) => ({ kind: k, meta: KIND_META[k], rows: items.filter((i) => i.kind === k) })).filter((g) => g.rows.length > 0);
  if (items.length === 0) return null;
  return (
    <div className="space-y-8 max-w-3xl" data-testid="ai-saved-notebook">
      {groups.map((g) => (
        <div key={g.kind}>
          <div className="flex items-center gap-2 mb-3">
            <g.meta.icon size={18} className="text-[#D4AF37]" />
            <h2 className="font-display font-bold text-[#0F0F0F]">{g.meta.label}</h2>
            <span className="text-xs bg-white border border-gray-100 rounded-full px-2 py-0.5 text-[#687280]">{g.rows.length}</span>
          </div>
          <div className="space-y-3">
            {g.rows.map((row) =>
              CHECKABLE.includes(g.kind) ? (
                <RoadmapCard key={row.id} row={row} onDelete={onDelete} onUpdate={onUpdate} />
              ) : (
                <SimpleCard key={row.id} row={row} onDelete={onDelete} />
              )
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

const titleFor = (row) => {
  const d = row.data || {};
  if (row.kind === "stack") return d.building || "AI Tool Stack";
  if (row.kind === "workflow") return d.name || "Workflow";
  if (row.kind === "tool") return d.best_tool || "AI Tool";
  if (row.kind === "plan") return d.name || "My Startup";
  if (row.kind === "competitor") return d.idea || "Competitor Analysis";
  if (row.kind === "market") return d.idea || "Market Size Estimate";
  if (row.kind === "name") return d.name || "Name Check";
  if (row.kind === "idea") return d.name || "Startup Idea";
  if (row.kind === "formation") return d.structure || "Business Formation Plan";
  return d.business_type || "Roadmap";
};
const subFor = (row) => {
  const d = row.data || {};
  if (row.kind === "stack") return `${(d.stack || []).length} tools · $${d.total_monthly_cost || 0}/mo`;
  if (row.kind === "workflow") return d.hours_saved ? `Saves ${d.hours_saved} hrs/week` : (d.description || "");
  if (row.kind === "tool") return d.price || d.why || "";
  if (row.kind === "competitor") return `${(d.competitors || []).length} competitors · Diff score ${d.differentiation_score || "—"}/100`;
  if (row.kind === "market") return `${Number(d.paying_customers || 0).toLocaleString()} customers · $${Number(d.annual_revenue || 0).toLocaleString()}/yr`;
  if (row.kind === "name") return `Brand score ${d.brand_score || "—"}/100`;
  if (row.kind === "idea") return d.ai_score ? `AI Score ${d.ai_score}/100 · ${d.revenue_model || ""}` : (d.description || "");
  if (row.kind === "formation") return d.platform?.name ? `Recommended: ${d.platform.name}` : "Formation plan";
  return `${(d.steps || []).length} steps`;
};

const SimpleCard = ({ row, onDelete }) => (
  <div className="bg-white rounded-2xl p-4 border border-gray-100 flex items-center gap-4" data-testid={`ai-saved-${row.id}`}>
    <div className="min-w-0 flex-1">
      <p className="font-display font-semibold text-[#0F0F0F] truncate">{titleFor(row)}</p>
      <p className="text-xs text-[#687280] mt-0.5 truncate">{subFor(row)}</p>
    </div>
    <button onClick={() => onDelete(row.id)} className="text-[#687280] hover:text-red-500 transition-colors shrink-0" data-testid={`ai-saved-delete-${row.id}`} title="Remove"><Trash2 size={16} /></button>
  </div>
);

const RoadmapCard = ({ row, onDelete, onUpdate }) => {
  const [open, setOpen] = useState(true);
  const [done, setDone] = useState(row.data?.done || []);
  const steps = row.data?.steps || [];
  const pct = steps.length ? Math.round((done.length / steps.length) * 100) : 0;
  const suffix = row.kind === "plan" ? "Validation Plan" : "Roadmap";
  const HeadIcon = row.kind === "plan" ? ListChecks : Map;

  const toggle = async (i) => {
    const next = done.includes(i) ? done.filter((x) => x !== i) : [...done, i];
    setDone(next);
    try { await onUpdate(row.id, { ...row.data, done: next }); }
    catch { toast.error("Could not save progress"); setDone(done); }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden" data-testid={`ai-saved-${row.id}`}>
      <div className="p-4 flex items-center gap-3">
        <span className="w-10 h-10 rounded-xl bg-[#0F0F0F] text-[#D4AF37] flex items-center justify-center shrink-0"><HeadIcon size={18} /></span>
        <button onClick={() => setOpen((o) => !o)} className="flex-1 text-left min-w-0" data-testid={`ai-roadmap-toggleopen-${row.id}`}>
          <p className="font-display font-bold text-[#0F0F0F] truncate">{titleFor(row)} {suffix}</p>
          <p className="text-xs text-[#687280]" data-testid={`ai-roadmap-progress-${row.id}`}>{done.length} of {steps.length} complete · {pct}%</p>
        </button>
        <ChevronDown size={18} className={`text-[#687280] transition-transform ${open ? "rotate-180" : ""}`} onClick={() => setOpen((o) => !o)} />
        <button onClick={() => onDelete(row.id)} className="text-[#687280] hover:text-red-500 transition-colors shrink-0" data-testid={`ai-saved-delete-${row.id}`} title="Remove"><Trash2 size={16} /></button>
      </div>
      <div className="px-4 pb-2">
        <div className="h-2 rounded-full bg-gray-100 overflow-hidden"><div className="h-full rounded-full bg-[#D4AF37] transition-all duration-500" style={{ width: `${pct}%` }} /></div>
      </div>
      {open && (
        <div className="p-4 pt-3 space-y-2.5 border-t border-gray-50">
          {steps.map((s, i) => {
            const isDone = done.includes(i);
            return (
              <div key={i} className={`rounded-xl border p-3 flex items-start gap-3 transition-colors ${isDone ? "border-green-200 bg-green-50/50" : "border-gray-100"}`} data-testid={`ai-roadmap-step-${row.id}-${i}`}>
                <button onClick={() => toggle(i)} data-testid={`ai-roadmap-toggle-${row.id}-${i}`} aria-pressed={isDone} className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 transition-colors ${isDone ? "bg-green-500 text-white" : "bg-[#0F0F0F] text-[#D4AF37] hover:bg-[#1c1c1c]"}`}>
                  {isDone ? <CheckCircle2 size={16} /> : i + 1}
                </button>
                <div className="min-w-0 flex-1">
                  <p className={`font-semibold text-sm ${isDone ? "text-[#687280] line-through" : "text-[#0F0F0F]"}`}>{s.title}</p>
                  <p className="text-xs text-[#687280] mt-0.5 leading-snug">{s.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
